import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getInitials } from "@/lib/Utils/getInitials";
import { useState, useMemo } from "react";
import { Input, InputGroup } from "@/components/catalyst/input";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { ShieldCheckIcon } from "@heroicons/react/24/solid";
import { NoOrgAlert } from "@/app/Organisations/Components/NoOrgAlert";
import { LinkButton } from "@/components/catalyst/button";
import { useAuth } from "@/lib/Auth/useAuth";
import { AuthOrganisation } from "@/lib/Auth/authApi";
import { useOrganisationSwitcher } from "@/app/Organisations/Hooks/useOrganisationSwitcher";

export function OrganisationPicker() {
  const [searchQuery, setSearchQuery] = useState("");
  const { organisations } = useAuth();
  const { switchOrganisation } = useOrganisationSwitcher();

  const filteredOrganisations = useMemo(() => {
    if (!organisations || !searchQuery.trim()) return organisations;

    return organisations.filter((org) =>
      org.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [organisations, searchQuery]);

  const showOrganisationList = (organisations?.length ?? 0) > 0;

  const hasAdminAccess = useMemo(() => {
    if (!organisations) return false;
    return organisations.some((org) => org.is_admin);
  }, [organisations]);

  const adminOrg = useMemo(() => {
    if (!organisations) return null;
    return organisations.find((org) => org.is_admin);
  }, [organisations]);

  if (!organisations) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <Spinner className="h-10 w-10 text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="">
      {hasAdminAccess && adminOrg ? (
        <button
          onClick={() => switchOrganisation(adminOrg)}
          className="focus:outline-hidden mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-sky-100 px-4 py-3 font-medium text-sky-800 transition-colors hover:bg-sky-200 focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
        >
          <ShieldCheckIcon className="h-5 w-5" />
          Login as Admin
        </button>
      ) : null}

      {showOrganisationList ? (
        <div className="mt-6 flex flex-col space-y-4">
          <div className="relative">
            <InputGroup>
              <MagnifyingGlassIcon />
              <Input
                type="text"
                placeholder="Search organizations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </div>

          <div className="rounded-md">
            {filteredOrganisations && filteredOrganisations.length > 0 ? (
              <ul className="divide-y divide-gray-100">
                {filteredOrganisations.map((org) => (
                  <OrgListItem
                    org={org}
                    key={org.id}
                    onClick={() => switchOrganisation(org)}
                  />
                ))}
              </ul>
            ) : (
              <div className="flex h-full items-center justify-center p-4 text-sm text-gray-500">
                {searchQuery
                  ? "No organizations found"
                  : "No organizations available"}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {!showOrganisationList ? <NoOrgAlert /> : null}

      <div className="mt-auto pt-6">
        <div className="relative">
          <div
            className="absolute inset-0 flex items-center"
            aria-hidden="true"
          >
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center text-sm font-medium leading-6">
            <span className="bg-white px-6 text-gray-500">Or</span>
          </div>
        </div>

        <LinkButton to="/onboarding/create-org" className="mt-6 w-full">
          Create New Organization
        </LinkButton>
      </div>
    </div>
  );
}

function OrgListItem({
  org,
  onClick,
}: {
  org: AuthOrganisation;
  onClick: () => void;
}) {
  const [isLoading, setIsLoading] = useState(false);

  return (
    <li className="w-full">
      <button
        disabled={isLoading}
        className="focus:outline-hidden flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 focus:bg-gray-50 disabled:opacity-50"
        onClick={async () => {
          setIsLoading(true);
          try {
            onClick();
          } finally {
            setIsLoading(false);
          }
        }}
      >
        {!isLoading ? (
          <Avatar className="h-10 w-10 border border-gray-200">
            {org.profile_picture_url ? (
              <AvatarImage
                src={org.profile_picture_url}
                alt={org.name}
                className="bg-white object-contain"
              />
            ) : null}
            <AvatarFallback className="bg-indigo-100 text-indigo-600">
              {getInitials(org.name)}
            </AvatarFallback>
          </Avatar>
        ) : (
          <div className="flex h-10 w-10 items-center justify-center">
            <Spinner className="h-5 w-5 text-indigo-600" />
          </div>
        )}
        <div className="flex-1">
          <p className="font-medium text-gray-900">{org.name}</p>
        </div>
      </button>
    </li>
  );
}
